import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilledButton from './FilledButton';
import { SURFACE_BORDER } from '../siop/tableChrome';

/**
 * The app's modal.
 *
 * One shape for every dialog, taken from the reference upload modal: a title
 * row, a hairline, the content, a hairline, and a footer whose actions sit
 * bottom-right — Cancel in grey, the confirming action in the app's primary.
 * Dialogs were each assembling that themselves out of DialogTitle /
 * DialogContent / DialogActions with their own paddings, so no two agreed on
 * the spacing or on which side the buttons went.
 *
 * MUI's `Dialog` is still underneath, and deliberately: it carries the portal,
 * the backdrop, the focus trap, the Escape handling and the ARIA wiring, and
 * hand-rolling those is how modals end up unreachable by keyboard. What is NOT
 * used is any of its default CHROME — the styling here is the app's own, and
 * the actions are FilledButton rather than MUI buttons.
 *
 * `actions` overrides the footer actions for the rare dialog that needs
 * something other than Cancel/confirm; `footerContent` replaces that action
 * cluster with a custom full-width footer; `hideFooter` drops it entirely for
 * read-only dialogs.
 * `leadingHeaderActions` sits before the title; `headerActions` sits after the
 * title and before the close button. Both are for chrome such as navigation,
 * never for the dialog's choices.
 */
const AppModal = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  // Footer, standard form.
  confirmLabel = 'Save',
  onConfirm,
  confirmDisabled = false,
  cancelLabel = 'Cancel',
  actions,
  footerContent,
  footerSx,
  hideFooter = false,
  maxWidth = 'sm',
  fullWidth = true,
  contentMinHeight,
  contentSx,
  leadingHeaderActions,
  headerActions,
  inlineSubtitle = false,
  compactTitle = false,
  hideTitle = false,
  hideClose = false,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth={maxWidth}
    fullWidth={fullWidth}
    slotProps={{
      paper: {
        sx: {
          borderRadius: '16px',
          // The theme's Paper shadow is a hairline, which is right for a card
          // on the page and too light for a surface floating over a scrim.
          boxShadow: '0 24px 48px -12px rgba(16, 24, 40, 0.28)',
        },
      },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: hideTitle ? 'flex-end' : undefined,
        gap: 1,
        px: 3,
        py: 2,
        borderBottom: `1px solid ${SURFACE_BORDER}`,
      }}
    >
      {leadingHeaderActions}
      {!hideTitle && (
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            ...(inlineSubtitle
              ? { display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'nowrap' }
              : {}),
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: compactTitle ? 15 : 18,
              fontWeight: 700,
              color: '#111827',
              ...(inlineSubtitle
                ? {
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }
                : {}),
              ...(compactTitle
                ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
                : {}),
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                mt: inlineSubtitle ? 0 : 0.25,
                fontSize: 12.5,
                color: '#6B7280',
                ...(inlineSubtitle ? { whiteSpace: 'nowrap' } : {}),
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      {headerActions}
      {!hideClose && (
        /* An icon button, not a FilledButton: it is chrome on the surface, not
           one of the dialog's choices — those live in the footer. */
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>

    <Box sx={{ px: 3, py: 2.5, minHeight: contentMinHeight, ...contentSx }}>{children}</Box>

    {!hideFooter && (
      <Box
        sx={{
          display: 'flex',
          justifyContent: footerContent ? 'stretch' : 'flex-end',
          gap: 1.5,
          px: 3,
          py: 2,
          borderTop: `1px solid ${SURFACE_BORDER}`,
          ...footerSx,
        }}
      >
        {footerContent ?? actions ?? (
          <>
            {/* Grey rather than a second blue pill: the two choices are not
                equally weighted, and colour is what says so. */}
            <FilledButton btnname={cancelLabel} color="#9AA3AF" onClick={onClose} />
            <FilledButton btnname={confirmLabel} onClick={onConfirm} disabled={confirmDisabled} />
          </>
        )}
      </Box>
    )}
  </Dialog>
);

export default AppModal;
